import React, { useState, useEffect } from 'react';
import { useCafeteriaSelection } from '../../../context/CafeteriaSelectionContext';
import { placeService } from '../../../services/api';
import LogoUpload from '../../../components/ui/LogoUpload';
import CoverImageUpload from '../../../components/ui/CoverImageUpload';
import MultiSelectChips from '../../../components/ui/MultiSelectChips';
import { Category, Characteristic } from '../../../types/categories';

interface PlaceData {
  id: string;
  name: string;
  description: string;
  address: string;
  city?: string;
  state?: string;
  instagram?: string;
  logoUrl?: string;
  coverImageUrl?: string;
  cityId: string;
}

const PlaceInfoPage = () => {
  const { currentCafeteria } = useCafeteriaSelection();
  const [placeData, setPlaceData] = useState<PlaceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    instagram: '',
    logoUrl: '',
    coverImageUrl: ''
  });

  // Categories and Characteristics state
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
  const [availableCharacteristics, setAvailableCharacteristics] = useState<Characteristic[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedCharacteristicIds, setSelectedCharacteristicIds] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [characteristicsLoading, setCharacteristicsLoading] = useState(false);

  // Estado inicial para controle de alterações
  const [initialFormData, setInitialFormData] = useState({
    name: '',
    description: '',
    address: '',
    instagram: '',
    logoUrl: '',
    coverImageUrl: ''
  });
  const [initialCategoryIds, setInitialCategoryIds] = useState<string[]>([]);
  const [initialCharacteristicIds, setInitialCharacteristicIds] = useState<string[]>([]);

  useEffect(() => {
    if (currentCafeteria) {
      loadPlaceData();
      loadCategoriesAndCharacteristics();
    }
  }, [currentCafeteria]);

  const loadPlaceData = async () => {
    if (!currentCafeteria) return;

    try {
      setIsLoading(true);
      setError('');
      const data = await placeService.getCurrentPlace(currentCafeteria.id);
      setPlaceData(data);
      
      // Preencher formulário com dados existentes
      const newFormData = {
        name: data.name || '',
        description: data.description || '',
        address: data.address || '',
        instagram: data.instagram || '',
        logoUrl: data.logoUrl || '',
        coverImageUrl: data.coverImageUrl || ''
      };
      
      setFormData(newFormData);
      setInitialFormData(newFormData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar dados da cafeteria');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategoriesAndCharacteristics = async () => {
    if (!currentCafeteria) return;

    try {
      setCategoriesLoading(true);
      setCharacteristicsLoading(true);

      // Carregar categorias e características disponíveis
      const [categories, characteristics] = await Promise.all([
        placeService.getCategories(),
        placeService.getCharacteristics()
      ]);

      setAvailableCategories(categories);
      setAvailableCharacteristics(characteristics);

      // Carregar categorias e características selecionadas do estabelecimento
      const [placeCategories, placeCharacteristics] = await Promise.all([
        placeService.getPlaceCategories(currentCafeteria.id),
        placeService.getPlaceCharacteristics(currentCafeteria.id)
      ]);

      const categoryIds = placeCategories.map(cat => cat.id);
      const characteristicIds = placeCharacteristics.map(char => char.id);
      
      setSelectedCategoryIds(categoryIds);
      setSelectedCharacteristicIds(characteristicIds);
      setInitialCategoryIds(categoryIds);
      setInitialCharacteristicIds(characteristicIds);

    } catch (err: any) {
      console.error('Erro ao carregar categorias e características:', err);
      setError('Erro ao carregar categorias e características');
    } finally {
      setCategoriesLoading(false);
      setCharacteristicsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Verificar se há alterações
  const hasChanges = () => {
    // Verificar alterações no formulário
    const formChanged = Object.keys(formData).some(key => 
      formData[key as keyof typeof formData] !== initialFormData[key as keyof typeof initialFormData]
    );

    // Verificar alterações nas categorias
    const categoriesChanged = JSON.stringify(selectedCategoryIds.sort()) !== JSON.stringify(initialCategoryIds.sort());

    // Verificar alterações nas características
    const characteristicsChanged = JSON.stringify(selectedCharacteristicIds.sort()) !== JSON.stringify(initialCharacteristicIds.sort());

    return formChanged || categoriesChanged || characteristicsChanged;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCafeteria) return;

    try {
      setIsSaving(true);
      setError('');
      setSuccessMessage('');
      
      // Atualizar dados básicos do estabelecimento
      await placeService.updatePlace(currentCafeteria.id, formData);
      
      // Atualizar categorias e características
      await Promise.all([
        placeService.updatePlaceCategories(currentCafeteria.id, selectedCategoryIds),
        placeService.updatePlaceCharacteristics(currentCafeteria.id, selectedCharacteristicIds)
      ]);
      
      setSuccessMessage('Informações atualizadas com sucesso!');

      // Atualizar estado inicial com os novos dados
      setInitialFormData(formData);
      setInitialCategoryIds(selectedCategoryIds);
      setInitialCharacteristicIds(selectedCharacteristicIds);

      // Recarregar dados
      await loadPlaceData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar alterações');
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentCafeteria) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Informações</h1>
          <p className="text-gray-600 mt-2">
            Selecione um estabelecimento para gerenciar as informações
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Informações</h1>
          <p className="text-gray-600 mt-2">
            Carregando dados da {currentCafeteria.name}...
          </p>
        </div>
        <div className="flex justify-center py-8">
          <div className="spinner w-8 h-8"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Sticky apenas quando há alterações */}
      <div className={`${hasChanges() ? 'sticky top-0 z-40 bg-white border-b border-gray-200 -mx-6 px-6 py-4 -mb-6' : ''}`}>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Informações</h1>
            <p className="text-gray-600 mt-2">
              Gerencie os dados e configurações da {currentCafeteria.name}
            </p>
          </div>
          <button 
            onClick={handleSubmit}
            disabled={isSaving || !hasChanges()}
            className={`btn ${hasChanges() ? 'btn-primary' : 'btn-secondary'} ${!hasChanges() ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações Básicas */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-800">
              Informações Básicas
            </h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="input"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="input"
                placeholder="Descreva sua cafeteria..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instagram
              </label>
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => handleInputChange('instagram', e.target.value)}
                className="input"
                placeholder="@sua_cafeteria"
              />
            </div>
          </div>
        </div>

        {/* Localização */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-800">
              Localização
            </h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endereço
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="input"
                required
                placeholder="Rua, número, bairro..."
              />
            </div>
            
            {placeData && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Cidade:</strong> {placeData.city || 'Não informada'}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Estado:</strong> {placeData.state || 'Não informado'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Para alterar a cidade, entre em contato com o suporte.
                </p>
              </div>
            )}
          </div>
        </div>

                {/* Imagens */}
                <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-800">
              Imagens
            </h3>
          </div>
          <div className="card-body space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Logo da Cafeteria
              </label>
              <LogoUpload
                onImageUploaded={(url) => handleInputChange('logoUrl', url)}
                currentImageUrl={formData.logoUrl}
              />
              <p className="text-xs text-gray-500 mt-1">
                Recomendado: imagem quadrada, mínimo 256x256px
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Imagem de Capa
              </label>
              <CoverImageUpload
                onImageUploaded={(url) => handleInputChange('coverImageUrl', url)}
                currentImageUrl={formData.coverImageUrl}
              />
              <p className="text-xs text-gray-500 mt-1">
                Recomendado: formato paisagem (16:9), mínimo 1280x720px
              </p>
            </div>
          </div>
        </div>

        {/* Categorias */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-800">
              Categorias
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Selecione as categorias que melhor descrevem seu estabelecimento
            </p>
          </div>
          <div className="card-body">
            {categoriesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner w-6 h-6"></div>
                <span className="ml-2 text-sm text-gray-500">Carregando categorias...</span>
              </div>
            ) : (
              <MultiSelectChips
                items={availableCategories}
                selectedIds={selectedCategoryIds}
                onSelectionChange={setSelectedCategoryIds}
                placeholder="Selecione as categorias..."
                disabled={isSaving}
              />
            )}
          </div>

          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-800">
              Características
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Selecione as características que seu estabelecimento oferece
            </p>
          </div>
          <div className="card-body">
            {characteristicsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner w-6 h-6"></div>
                <span className="ml-2 text-sm text-gray-500">Carregando características...</span>
              </div>
            ) : (
              <MultiSelectChips
                items={availableCharacteristics}
                selectedIds={selectedCharacteristicIds}
                onSelectionChange={setSelectedCharacteristicIds}
                placeholder="Selecione as características..."
                disabled={isSaving}
              />
            )}
          </div>
        </div>

        {/* Características */}
        <div className="card">
          
        </div>

      </form>
    </div>
  );
};

export default PlaceInfoPage; 